import React, { useState, useEffect, useMemo, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";
import { Box, Typography, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { SolidButton } from "components/buttons/SolidButton";
import DrawerField from "components/Drawer";
import TableField from "components/Table";
import GlobalAlert from "components/Notification";
import ModalContainer from "components/ModalContainer";
import ModalforCheck from "../../modalCheck";
import { Loading } from "components/Loading";

// Icons
import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";

// Hooks
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

// Styles & Helpers
import styles from "./mainStyle.module.scss";
import CreateRuleModal from "./components/CreateRuleModal";
import SearchInput from "components/SearchInput";
import { getRebootTableColumns } from "./const";
import {
  getApiUrlAndParams,
  getDevicesApiUrlAndParams,
} from "./helper";

const ScheduledReboot = ({ showEditGeneral = true }) => {
  const theme = useTheme();
  const { sendBatch } = useDataSourceManager();

  const [rulesList, setRulesList] = useState([]);
  const [devicesList, setDevicesList] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteRuleId, setDeleteRuleId] = useState(null);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: null,
    message: null,
  });

  const fetchRulesAndDevices = async () => {
    setIsLoading(true);
    const uuids = {
      rules: uuidv4(),
      devices: uuidv4()
    };

    const { apiUrl: rulesApiUrl, restfulParams: rulesRestfulParams } = getApiUrlAndParams("read");
    const { apiUrl: devicesApiUrl, restfulParams: devicesRestfulParams } = getDevicesApiUrlAndParams();

    const { getSendBatchData } = await sendBatch([
      {
        apiKey: rulesApiUrl,
        requestUuid: uuids.rules,
        restfulPlaceholders: rulesRestfulParams,
      },
      {
        apiKey: devicesApiUrl,
        requestUuid: uuids.devices,
        restfulPlaceholders: devicesRestfulParams,
      },
    ]);

    const rulesResp = getSendBatchData({ requestUuid: uuids.rules });
    const devicesResp = getSendBatchData({ requestUuid: uuids.devices });

    if (rulesResp && !rulesResp.code) {
      setRulesList(rulesResp.data || []);
    }
    if (devicesResp && !devicesResp.code) {
      setDevicesList(devicesResp.data || []);
    }
    setIsLoading(false);
  };

  const filteredRules = useMemo(() => {
    return rulesList.filter((rule) =>
      (rule.name || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [rulesList, search]);

  const handleOpenAddModal = () => {
    setSelectedRule(null);
    setModalOpen(true);
  };

  const handleRowClick = (rule) => {
    setSelectedRule(rule);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedRule(null);
  };

  const handleApplyChanges = async (rulePayload) => {
    setIsLoading(true);
    setDrawerOpen(false);
    setModalOpen(false);
    const isEdit = !!rulePayload.id;
    const action = isEdit ? "update" : "create";
    const { apiUrl, restfulParams } = getApiUrlAndParams(action);

    if (isEdit) {
      restfulParams.scheduleRebootId = rulePayload.id;
    }

    const opUuid = uuidv4();
    const { getSendBatchData } = await sendBatch([
      {
        apiKey: apiUrl,
        requestUuid: opUuid,
        restfulPlaceholders: restfulParams,
        params: { data: rulePayload },
      },
    ]);

    const resp = getSendBatchData({ requestUuid: opUuid });

    if (resp && resp.code) {
      setAlertInfo({
        open: true,
        severity: "error",
        message: resp.message || `Failed to ${action} reboot rule.`,
      });
      setIsLoading(false);
    } else if (resp) {
      fetchRulesAndDevices();
    }
  };

  const handleDeleteTrigger = (ruleId) => {
    setDeleteRuleId(ruleId);
    setOpenDeleteModal(true);
  };

  const handleDeleteConfirm = async (value) => {
    setOpenDeleteModal(false);
    if (value === "ok" && deleteRuleId) {
      setIsLoading(true);
      setDrawerOpen(false);
      const { apiUrl, restfulParams } = getApiUrlAndParams("delete");
      const opUuid = uuidv4();

      const { getSendBatchData } = await sendBatch([
        {
          apiKey: apiUrl,
          requestUuid: opUuid,
          restfulPlaceholders: restfulParams,
          params: { data: { ids: [deleteRuleId] } },
        },
      ]);

      const resp = getSendBatchData({ requestUuid: opUuid });

      if (resp && resp.code) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: resp.message || "Failed to remove Schedule Reboot Rule.",
        });
        setIsLoading(false);
      } else if (resp) {
        fetchRulesAndDevices();
      }

      setDeleteRuleId(null);
    }
  };

  const columnHelper = useMemo(() => createColumnHelper(), []);
  const columns = useMemo(
    () => getRebootTableColumns({ columnHelper, styles, handleRowClick, devicesList }),
    [columnHelper, handleRowClick, devicesList]
  );

  const tableInstance = useTableInstance({
    tableName: "scheduleReboot",
    data: filteredRules,
    columns: columns,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
  });

  useEffect(() => {
    fetchRulesAndDevices();
  }, []);

  return (
    <div className={styles.container}>
      {isLoading && <Loading />}
      {/* Header Bar */}
      <Box className={styles["header-bar"]}>
        <Box>
          {showEditGeneral && (
            <SolidButton
              width="120px"
              height="32px"
              onClick={handleOpenAddModal}
              startIcon={<AddIcon style={{ width: 14, height: 14 }} />}
            >
              Add Rule
            </SolidButton>
          )}
        </Box>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by Name"
        />
      </Box>

      {/* Main Table */}
      <TableField
        {...tableInstance}
        drawerOpen={drawerOpen}
        handleRowOnClick={(row) => handleRowClick(row)}
        pendingChildren={isLoading && <Loading />}
      />

      {/* Edit Drawer */}
      {drawerOpen && (
        <DrawerField
          open={drawerOpen}
          handleClose={handleDrawerClose}
          width={620}
          contentKey={`schedule_reboot_drawer_${selectedRule?.id || "edit"}`}
          commonContent="settingsScheduledReboot"
          contentProps={{
            selectedRule,
            onClose: handleDrawerClose,
            onApply: handleApplyChanges,
            onDelete: handleDeleteTrigger,
            showEditGeneral,
            devicesList,
          }}
        />
      )}

      {/* Creation Modal */}
      <CreateRuleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onApply={handleApplyChanges}
        showEditGeneral={showEditGeneral}
        devicesList={devicesList}
      />

      {/* Remove Confirmation Dialog */}
      <ModalContainer
        display={openDeleteModal}
        modalWidth="500px"
        children={
          <ModalforCheck
            title="Remove Profile"
            content={`Are you sure you want to remove "${rulesList.find((r) => r.id === deleteRuleId)?.name || ""
              }"?`}
            cancelBtn="Cancel"
            saveBtn="Remove"
            setOpenModal={setOpenDeleteModal}
            onBottonClick={handleDeleteConfirm}
          />
        }
      />

      {/* Global Alerts */}
      <GlobalAlert
        open={alertInfo.open}
        severity={alertInfo.severity}
        message={alertInfo.message}
        onClose={() => setAlertInfo((prev) => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default ScheduledReboot;
